import { last } from "common-utils/utils/array";
import { httpGet$GetAuctionBids } from "kreate-server/commands/GetAuctionBids/fetcher";
import {
  GetAuctionBids$Params,
  GetAuctionBids$Result,
} from "kreate-server/commands/GetAuctionBids/typing";
import useSWRInfinite from "swr/infinite";

type PageData = {
  result: GetAuctionBids$Result;
  nextOffset: number | undefined;
};

export function useAuctionBidListInfinite(
  params: Omit<GetAuctionBids$Params, "offset"> | null | undefined
) {
  const swr = useSWRInfinite(
    (pageIndex: number, previousPageData: PageData | undefined) => {
      if (!params) return null;
      const thisPageParams: GetAuctionBids$Params | undefined =
        pageIndex === 0
          ? { ...params, offset: 0 }
          : previousPageData?.nextOffset != null
          ? { ...params, offset: previousPageData.nextOffset }
          : undefined;

      return ["7dddf909-3ec7-4062-a848-e49344ccdb63", thisPageParams];
    },
    async ([_, params]: [string, GetAuctionBids$Params]): Promise<PageData> => {
      const result = await httpGet$GetAuctionBids(
        "/api/v1/all-auctions/auction-bids",
        params
      );
      const nextOffset =
        params.limit != null &&
        params.offset != null &&
        result.auctionBids.length >= params.limit
          ? params.offset + result.auctionBids.length
          : undefined;
      return { result, nextOffset };
    }
  );

  const isEnded = !swr.isLoading && swr.size > 0 && !last(swr.data)?.nextOffset;

  const loadMore =
    !swr.isLoading && !isEnded ? () => swr.setSize((s) => s + 1) : undefined;

  const isEmpty =
    !swr.isLoading &&
    swr.size > 0 &&
    swr.data?.every((d) => !d.result.auctionBids.length);

  const totalCount = last(swr.data)?.result.count;

  const isLoadingMore =
    swr.isLoading || (!!swr.data && swr.size > swr.data.length);

  return {
    data: swr.data?.map((d) => d.result) satisfies
      | GetAuctionBids$Result[]
      | undefined,
    error: swr.error,
    isLoading: swr.isLoading,
    size: swr.size,
    loadMore,
    computed: { isEnded, isEmpty, totalCount, isLoadingMore },
    params,
  };
}
